import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { PublicStats } from "@/components/PublicStats";
import {
  Sparkles,
  ArrowRight,
  Check,
  School,
  Accessibility,
  Volume2,
  Languages,
  Zap,
  Users,
  Mail,
  BookOpen,
  Brain,
} from "lucide-react";

/**
 * Institutional landing page targeting French education establishments:
 * schools, colleges, high schools, universities. Angles: (1) inclusive
 * accessibility (dys, ADHD, allophones, visually impaired), (2) cost
 * (prepaid credit packs — paid model after 3 free summaries).
 *
 * URL: /education — indexed by Google on queries like "outil résumé
 * élèves dys", "aide aux élèves allophones IA", "outil pédagogique
 * accessible collège".
 */

const ProfileCard = ({ icon: Icon, title, body, testid }) => (
  <div
    className="border border-border bg-card p-6 flex gap-4"
    data-testid={testid}
  >
    <div className="w-10 h-10 bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h3 className="font-semibold text-base mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  </div>
);

export default function Education() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const isEn = i18n.resolvedLanguage === "en";
  const guideHref = isEn ? "/guide-en.html" : "/guide.html";

  useEffect(() => {
    const previousTitle = document.title;
    const previousDesc = document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content");

    document.title =
      "SUX1 pour l'Éducation — Outil de résumé accessible pour élèves dys, TDA/H et allophones";

    const descMeta = document.querySelector('meta[name="description"]');
    const descContent =
      "SUX1 est un outil de résumé de textes pensé pour les élèves à besoins particuliers : dyslexiques, TDA/H, allophones, malvoyants. Utilisable en école, collège, lycée, université. packs de crédits dès 2 € les 20 résumés — paiement unique, sans abonnement.";
    if (descMeta) descMeta.setAttribute("content", descContent);

    return () => {
      document.title = previousTitle;
      if (descMeta && previousDesc) {
        descMeta.setAttribute("content", previousDesc);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-card">
      {/* Hero */}
      <section
        className="border-b border-border bg-gradient-to-b from-secondary/30 to-white"
        data-testid="education-hero"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 lg:py-28">
          <div className="sux-marker mb-4 flex items-center gap-2">
            <School className="w-3.5 h-3.5" />
            ÉCOLES · COLLÈGES · LYCÉES · UNIVERSITÉS
          </div>
          <h1
            className="text-5xl md:text-7xl font-black tracking-tight leading-[0.95] mb-6"
            style={{ fontFamily: "Outfit" }}
          >
            Un outil de résumé
            <br />
            <span className="text-[#002FA7]">accessible à tous vos élèves.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-10">
            SUX1 transforme un PDF, un chapitre, une thèse ou un article en
            un résumé fidèle en 5 secondes. Pensé dès l'origine pour les
            élèves <strong className="text-foreground">dyslexiques, TDA/H, allophones et malvoyants</strong> —
            avec des options concrètes que ChatGPT ne propose pas : phrases
            courtes, mots-clés en gras, lecture vocale native, adaptation
            de langue et de niveau.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={user ? "/" : "/signup"}
              data-testid="education-cta-try"
              className="bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs px-6 py-4 hover:bg-[#00227A] transition-colors flex items-center gap-2"
            >
              Voir les exemples gratuits
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <a
              href="mailto:fiestamax@gmail.com?subject=SUX1 %C3%A9tablissement scolaire&body=Bonjour,%0A%0AJe souhaite en savoir plus sur SUX1 pour notre %C3%A9tablissement.%0A%0ANom de l'%C3%A9tablissement :%0AVille :%0AFonction :%0A%0ACordialement,"
              data-testid="education-cta-contact"
              className="bg-card text-foreground border border-border font-mono uppercase tracking-[0.25em] text-xs px-6 py-4 hover:bg-[#FF2A00] hover:text-white transition-colors flex items-center gap-2"
            >
              <Mail className="w-3.5 h-3.5" />
              Nous contacter
            </a>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
            <a
              href="/education-plaquette.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="education-cta-plaquette"
              className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] hover:underline"
            >
              <BookOpen className="w-3.5 h-3.5" />
              Télécharger la plaquette PDF (1 page, imprimable)
              <ArrowRight className="w-3 h-3" />
            </a>
            <a
              href={guideHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="education-cta-guide"
              className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] hover:underline"
            >
              <BookOpen className="w-3.5 h-3.5" />
              Guide d'utilisation complet (2 pages, imprimable)
              <ArrowRight className="w-3 h-3" />
            </a>
          </div>

          <PublicStats variant="hero" />
        </div>
      </section>

      {/* Accessibility profiles */}
      <section
        className="border-b border-border bg-white"
        data-testid="education-profiles"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="sux-marker mb-3 flex items-center gap-2">
            <Accessibility className="w-3.5 h-3.5" />
            01 · UN OUTIL POUR CHAQUE SINGULARITÉ COGNITIVE
          </div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-10 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Six profils. Un seul outil.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProfileCard
              testid="education-profile-dys"
              icon={Brain}
              title="Élèves dys (dyslexie, dyspraxie, dysorthographie)"
              body="Chaque résumé peut être généré en phrases courtes (max 15 mots), avec les mots-clés en gras et une structure aérée. Un manuel de 200 pages devient une fiche claire lisible en 3 minutes."
            />
            <ProfileCard
              testid="education-profile-tdah"
              icon={Zap}
              title="Élèves TDA/H"
              body="Format « Points clés » avec puces courtes et hiérarchisées. Fin d'un chapitre en 1 écran. L'attention n'a plus à tenir sur 40 pages — elle tient sur 40 secondes."
            />
            <ProfileCard
              testid="education-profile-allophones"
              icon={Languages}
              title="Élèves allophones (UPE2A, primo-arrivants)"
              body="Choix de la langue de sortie : français, anglais, espagnol, allemand, italien, portugais. Un texte peut être résumé en français simplifié niveau A2 avec définitions des concepts entre parenthèses."
            />
            <ProfileCard
              testid="education-profile-mal"
              icon={Volume2}
              title="Élèves malvoyants ou en fatigue oculaire"
              body="Bouton « Écouter » sur chaque résumé — voix française et anglaise natives, disponibles hors ligne. Un chapitre entier devient un podcast à écouter."
            />
            <ProfileCard
              testid="education-profile-hpi"
              icon={Sparkles}
              title="Élèves HPI en soif de lecture"
              body="Traite jusqu'à 300 000 caractères d'un coup : livre entier, roman, bibliographie complète. Idéal pour des élèves qui veulent aller plus loin sans être limités par le temps de lecture."
            />
            <ProfileCard
              testid="education-profile-fatigue"
              icon={BookOpen}
              title="Élèves en fatigue cognitive (cancer, maladie longue durée)"
              body="Historique persistant : plus besoin de reprendre à zéro. Les résumés restent accessibles, recherchables, exportables. Un élève absent peut rattraper 3 mois de cours en un après-midi."
            />
          </div>
        </div>
      </section>

      {/* Concrete use cases for institutions */}
      <section
        className="border-b border-border bg-secondary/30"
        data-testid="education-usecases"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="sux-marker mb-3">02 · TROIS CAS D'USAGE ÉTABLISSEMENT</div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-8 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Comment vos équipes vont l'utiliser dès demain.
          </h2>

          <ul className="space-y-6">
            {[
              [
                "Le CDI / la BU",
                "Les documentalistes peuvent installer SUX1 sur les postes du CDI ou de la BU. Un élève arrive avec un article scientifique de 40 pages : en 30 secondes, il a une fiche de lecture adaptée à son niveau. Coût : dès 2 € les 20 résumés, sans abonnement.",
              ],
              [
                "Le référent accessibilité / ULIS",
                "Le référent utilise SUX1 en amont pour préparer, pour chaque élève à profil particulier, une version adaptée du texte source du cours. Ce qui prenait 45 minutes par élève par cours prend 2 minutes. Gain : plusieurs heures par semaine.",
              ],
              [
                "Le professeur de discipline",
                "Le prof d'histoire, de SVT, de français utilise SUX1 pour générer des supports différenciés à trois niveaux à partir d'un même texte : version standard, version simplifiée dys, version française simplifiée pour allophones. Une seule séance, trois publics servis.",
              ],
            ].map(([title, body]) => (
              <li
                key={title}
                className="border-l-4 border-[#002FA7] pl-4 py-1"
                data-testid="education-usecase-item"
              >
                <div className="font-semibold text-base mb-1">{title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {body}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Pricing + free offer */}
      <section
        className="border-b border-border bg-white"
        data-testid="education-pricing"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="sux-marker mb-3">03 · UN MODÈLE ÉCONOMIQUE INÉDIT</div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-6 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Dès 2 €. Sans abonnement. Sans expiration.
          </h2>
          <p className="text-base leading-relaxed mb-6">
            Pas d'abonnement mensuel. Pas de licence institutionnelle
            complexe. Pas de renouvellement à négocier chaque année. Chaque
            enseignant, chaque élève, chaque poste : un pack de crédits
            dès 2 € les 20 résumés, utilisable sans date limite.
          </p>
          <p className="text-base leading-relaxed mb-6">
            <strong className="text-foreground">Pour comparaison</strong> : ChatGPT Plus coûte
            240 $ par utilisateur et par an. Sur 10 ans, pour un
            établissement de 500 enseignants, cela ferait 1,2 million de
            dollars. SUX1 : on ne paie que les résumés réellement utilisés.
          </p>

          <div className="border-2 border-[#002FA7] bg-[#002FA7]/5 p-6 mt-8">
            <div className="sux-marker text-[#002FA7] mb-2 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" />
              LE PRIX D'UN CAFÉ
            </div>
            <h3
              className="text-2xl font-black tracking-tight mb-3"
              style={{ fontFamily: "Outfit" }}
            >
              Pour le prix d'un café, 20 résumés pour chaque élève.
            </h3>
            <p className="text-base leading-relaxed mb-4">
              Exemples gratuits pour découvrir, puis <strong className="text-foreground">des packs de
              crédits dès 2 € les 20 résumés</strong>. Aucun abonnement, aucune reconduction,
              aucun engagement institutionnel. Chaque enseignant, chaque élève,
              chaque poste : un paiement unique, des crédits qui n'expirent
              jamais (packs de 20, 60 ou 130 crédits). Facture Stripe automatique téléchargeable pour
              remboursement en frais professionnels.
            </p>
            <Link
              to={user ? "/" : "/signup"}
              data-testid="education-cta-offer"
              className="inline-flex items-center gap-2 bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs px-6 py-3 hover:bg-[#00227A] transition-colors"
            >
              Voir les exemples gratuits
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Institution FAQ */}
      <section
        className="border-b border-border bg-secondary/30"
        data-testid="education-faq"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="sux-marker mb-3">04 · VOS QUESTIONS</div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-8 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Réponses aux questions des chefs d'établissement.
          </h2>
          <div className="space-y-6">
            {[
              [
                "SUX1 est-il conforme au RGPD ?",
                "Oui. Aucune donnée utilisateur n'est utilisée pour entraîner de modèle. Les textes transmis pour résumé transitent chiffrés (HTTPS) vers les API des fournisseurs de modèles (Anthropic, OpenAI, Google) qui sont eux-mêmes RGPD-compatibles pour un usage éducatif. Les élèves et enseignants peuvent supprimer leur compte et l'intégralité de leurs données à tout moment.",
              ],
              [
                "Peut-on l'utiliser sur les postes de l'établissement ?",
                "Oui, SUX1 est une application web (aucune installation) et une PWA (installable comme une application native sur Android, Windows, macOS). Elle fonctionne sur tout navigateur moderne (Chrome, Firefox, Safari, Edge).",
              ],
              [
                "Facturation : peut-on obtenir une facture pour l'établissement ?",
                "Oui. Chaque achat de pack génère automatiquement une facture téléchargeable via Stripe, incluant nom de l'établissement et numéro de TVA intracommunautaire si fournis. Contactez-nous pour une facture groupée en cas de commande multi-utilisateurs.",
              ],
              [
                "Existe-t-il un accompagnement pédagogique ?",
                "Un livre pratique (à paraître, ~200 pages, KDP) présente 50 cas d'usage détaillés pour les enseignants, incluant les usages pour élèves à besoins particuliers.",
              ],
              [
                "L'outil est-il utilisable en autonomie par les élèves ?",
                "Oui, dès le collège. L'interface est en français, en anglais, et volontairement épurée (un seul écran, un seul bouton). Le format des résumés est paramétrable par l'élève selon ses besoins (longueur, langue, style).",
              ],
            ].map(([q, a]) => (
              <div
                key={q}
                className="border border-border bg-card p-6"
                data-testid="education-faq-item"
              >
                <div className="font-semibold text-base mb-2">{q}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="bg-[#002FA7] text-white"
        data-testid="education-cta-final"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 text-center">
          <div className="sux-marker mb-4 text-white/60">
            05 · PRENONS CONTACT
          </div>
          <h2
            className="text-4xl md:text-5xl font-black tracking-tight mb-6 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Écrivez-nous.
            <br />
            <span className="text-white/90">Nous répondons sous 48 h.</span>
          </h2>
          <p className="text-lg text-white/80 max-w-xl mx-auto mb-10 leading-relaxed">
            Une question sur l'outil, une envie d'organiser une démonstration
            pour votre équipe pédagogique, ou une demande de facture groupée
            multi-utilisateurs ?
          </p>
          <a
            href="mailto:fiestamax@gmail.com?subject=SUX1 %C3%A9tablissement scolaire"
            data-testid="education-cta-final-mail"
            className="inline-flex items-center gap-2 bg-white text-[#002FA7] font-mono uppercase tracking-[0.25em] text-xs px-8 py-5 hover:bg-white/90 transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            fiestamax@gmail.com
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs font-mono uppercase tracking-[0.2em] text-white/60">
            <span className="flex items-center gap-1.5">
              <Users className="w-3 h-3" />
              Écoles · Collèges · Lycées · Universités
            </span>
          </div>
        </div>
      </section>

      {/* Footer nav — À propos + Contact (consistent across public pages) */}
      <footer className="py-8 px-6 md:px-10 text-center border-t border-border bg-card">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <Link
            to="/about"
            data-testid="education-footer-about"
            className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            À propos
          </Link>
          <a
            href="mailto:fiestamax@gmail.com?subject=%5BSUX1%5D%20Contact"
            data-testid="education-footer-contact"
            className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            Contact
          </a>
        </div>
      </footer>
    </div>
  );
}
